import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  weight: string;
  price: number;
  image: string;
  alt: string;
}

interface OrderDetailsCardProps {
  orderId: string;
  orderDate: string;
  items: OrderItem[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  deliveryAddress: string;
  estimatedDelivery: string;
}

const OrderDetailsCard = ({
  orderId,
  orderDate,
  items,
  subtotal,
  deliveryFee,
  total,
  deliveryAddress,
  estimatedDelivery,
}: OrderDetailsCardProps) => {
  return (
    <div className="bg-card rounded-lg shadow-elevation-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl font-semibold text-foreground">Order Details</h2>
        <div className="text-right">
          <p className="font-data text-sm text-muted-foreground">Order ID</p>
          <p className="font-data font-medium text-foreground">{orderId}</p>
        </div>
      </div>

      {/* Order Items */}
      <div className="space-y-4 mb-6">
        {items.map((item) => (
          <div key={item.id} className="flex gap-4 p-4 bg-muted rounded-lg">
            <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
              <AppImage
                src={item.image}
                alt={item.alt}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-foreground">{item.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {item.quantity} × {item.weight}
              </p>
              <p className="font-data font-semibold text-primary mt-2">₹{item.price.toFixed(2)}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Price Breakdown */}
      <div className="border-t border-border pt-4 space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="font-data font-medium text-foreground">₹{subtotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Delivery Fee</span>
          <span className="font-data font-medium text-foreground">₹{deliveryFee.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-base font-semibold pt-3 border-t border-border">
          <span className="text-foreground">Total Amount</span>
          <span className="font-data text-primary">₹{total.toFixed(2)}</span>
        </div>
      </div>

      {/* Delivery Information */}
      <div className="mt-6 pt-6 border-t border-border space-y-4">
        <div className="flex gap-3">
          <Icon name="MapPinIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground mb-1">Delivery Address</p>
            <p className="text-sm text-muted-foreground">{deliveryAddress}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Icon name="ClockIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground mb-1">Estimated Delivery</p>
            <p className="text-sm text-muted-foreground">{estimatedDelivery}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Icon name="CalendarIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-foreground mb-1">Order Date</p>
            <p className="text-sm text-muted-foreground">{orderDate}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsCard;